import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { db, type DatabaseProfile, type UserMeal } from './database';
import { DatabaseService } from './database';
import type { ShoppingList, ShoppingListItem } from './database';

export type FoodItem = {
  id: string;
  name: string;
  category: string;
  location: 'fridge' | 'pantry' | 'freezer';
  quantity: number;
  unit: string;
  expiryDate: string;
  emoji: string;
};

export type Recipe = {
  id: string;
  title: string;
  description?: string;
  ingredients: Array<{
    name: string;
    amount: string;
    category: string;
    fromInventory: boolean;
  }>;
  time: number; // in minutes (cook_time from database)
  prepTime?: number;
  servings?: number;
  calories: number;
  nutrition?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
    sodium: number;
  };
  difficulty: 'easy' | 'medium' | 'hard';
  mealType?: string;
  cuisineStyle?: string;
  spiceLevel?: 'mild' | 'medium' | 'spicy';
  estimatedCost?: number;
  tags?: string[];
  dietary_preferences?: string[];
  user_allergies?: string[];
  isFavorite: boolean;
  imageUrl: string;
  thumbnailUrl?: string; // 🆕 Optimized thumbnail for fast loading in lists and modals
  createdAt: string; // Added for sorting by creation date
};

type UserProfile = {
  name: string;
  email: string;
  avatar: string;
  points: number;
  level: number;
  preferences: string[];
  allergies: string[];
  plan: 'free' | 'pro';
  apiProvider: 'openai' | 'openrouter';
  measurementSystem: 'metric' | 'imperial';
  floatingBarTheme: 'light' | 'dark';
  isAdmin: boolean;
  // Body Stats & Goals
  age?: number;
  gender?: 'male' | 'female' | 'other';
  height?: number; // in cm
  weight?: number; // in kg
  targetWeight?: number; // in kg
  goal?: 'lose' | 'maintain' | 'gain';
  activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  dailyCalorieTarget?: number; // calculated based on other stats
};

type LoadingState = {
  inventory: boolean;
  recipes: boolean;
  user: boolean;
  expiringItems: boolean;
  meals: boolean;
};

type DataFreshness = {
  inventory: number;
  recipes: number;
  user: number;
  expiringItems: number;
  meals: number;
};

type NoshState = {
  theme: 'light' | 'dark';
  user: UserProfile;
  inventory: FoodItem[];
  recipes: Recipe[];
  expiringItems: FoodItem[];
  meals: UserMeal[];
  loading: LoadingState;
  dataFreshness: DataFreshness;
  isOnline: boolean;
  
  // Nutrition Update Event System
  nutritionUpdateTrigger: number; // Timestamp to trigger nutrition component updates
  
  // Global Image Recognition Progress State
  imageRecognition: {
    isActive: boolean;
    showModal: boolean;
    currentPhase: string;
    elapsedTime: number;
    estimatedTime: number;
    startTime: number;
    scanType: 'inventory' | 'meal';
    error: string | null;
    // Tracking image upload path for cleanup
    imagePath?: string;
    // Progress update interval ID for cleanup
    intervalId?: NodeJS.Timeout;
  };
  
  // Global AI Usage Limit Modal State
  usageLimitModal: {
    isOpen: boolean;
    limits: any | null; // AIUsageLimits from aiUsageService
    featureType: string | null; // AIFeatureType
  };
  
  // Global Nutrition Analysis State
  nutritionAnalysis: {
    isActive: boolean;
    showModal: boolean;
    mealData: any | null;
    imageUrl: string;
    imagePath: string;
    timestamp: string;
    error: string | null;
    mode: 'analysis' | 'view';
  };
  
  // Global Scan Results State (for inventory scanning)
  scanResults: {
    isActive: boolean;
    showModal: boolean;
    scanData: any | null;
    imageUrl: string;
    imagePath: string;
    timestamp: string;
    error: string | null;
  };
  
  // Global Recipe Generation State
  recipeGeneration: {
    isActive: boolean;
    showModal: boolean;
    showResultModal: boolean;
    currentPhase: string;
    elapsedTime: number;
    estimatedTime: number;
    startTime: number;
    error: string | null;
    generatedRecipe: any | null;
    generationOptions: any | null;
    hasAnimated: boolean;
    // Progress update interval ID for cleanup
    intervalId?: NodeJS.Timeout;
    // NEW: Image Generation State (Pure Addition)
    imageGeneration: {
      isGenerating: boolean;
      imageUrl: string | null;
      error: string | null;
    };
  };
  
  // Ingredient Source Selection State
  ingredientSource: {
    showModal: boolean;
    selectedSource: 'inventory' | 'mixed' | 'photo' | null;
    detectedIngredients: string[];
    detectionId: string | null;
  };
  
  // Actions
  loadInventory: (force?: boolean) => Promise<void>;
  loadRecipes: (force?: boolean) => Promise<void>;
  loadUserProfile: (force?: boolean) => Promise<void>;
  loadExpiringItems: (force?: boolean) => Promise<void>;
  loadMeals: (force?: boolean) => Promise<void>;
  loadAllData: (force?: boolean) => Promise<void>;
  checkOnlineStatus: () => Promise<void>;
  isDataFresh: (dataType: keyof DataFreshness, maxAgeMinutes?: number) => boolean;
  
  addItem: (item: Omit<FoodItem, 'id'>) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  updateItem: (id: string, item: Partial<FoodItem>) => Promise<void>;
  toggleFavoriteRecipe: (id: string) => Promise<void>;
  updateUserPoints: (points: number) => Promise<void>;
  updateUserAvatar: (avatarUrl: string) => Promise<void>;
  updateUserApiProvider: (provider: 'openai' | 'openrouter') => void;
  updateUserMeasurementSystem: (system: 'metric' | 'imperial') => Promise<void>;
  updateFloatingBarTheme: (theme: 'light' | 'dark') => Promise<void>;
  updateUserPreferences: (preferences: string[]) => Promise<void>;
  updateUserAllergies: (allergies: string[]) => Promise<void>;
  updateUserName: (name: string) => Promise<void>;
  migrateUserData: () => Promise<boolean>;
  resetUserProfile: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;
  
  // Global Image Recognition Actions
  startImageRecognition: (scanType: 'inventory' | 'meal') => void;
  updateImageRecognitionProgress: (phase: string, elapsed: number) => void;
  setImageRecognitionError: (error: string) => void;
  completeImageRecognition: () => void;
  cancelImageRecognition: () => void;
  hideImageRecognitionModal: () => void;
  setImageRecognitionPath: (path: string) => void;
  
  // Global AI Usage Limit Modal Actions
  showUsageLimitModal: (limits: any, featureType: string) => void;
  hideUsageLimitModal: () => void;

  // Global Nutrition Analysis Actions
  showNutritionAnalysis: (data: {
    mealData: any;
    imageUrl: string;
    imagePath: string;
    mode?: 'analysis' | 'view';
  }) => void;
  hideNutritionAnalysis: () => void;
  setNutritionAnalysisError: (error: string) => void;
  clearNutritionAnalysis: () => void;
  
  // Global Scan Results Actions
  showScanResults: (data: {
    scanData: any;
    imageUrl: string;
    imagePath: string;
  }) => void;
  hideScanResults: () => void;
  setScanResultsError: (error: string) => void;
  clearScanResults: () => void;
  
  // Global Recipe Generation Actions
  startRecipeGeneration: () => void;
  updateRecipeGenerationProgress: (phase: string, elapsed: number) => void;
  setRecipeGenerationError: (error: string) => void;
  completeRecipeGeneration: (recipe: any) => void;
  
  // NEW: Recipe Image Generation Actions (Pure Addition)
  generateRecipeImage: (recipeData: any) => Promise<void>;
  setRecipeImageGenerating: (isGenerating: boolean) => void;
  setRecipeImageUrl: (imageUrl: string) => void;
  setRecipeImageError: (error: string) => void;
  clearRecipeImage: () => void;
  
  // Ingredient Source Selection Actions
  showIngredientSourceModal: () => void;
  hideIngredientSourceModal: () => void;
  setSelectedIngredientSource: (source: 'inventory' | 'mixed' | 'photo', detectedIngredients?: string[], detectionId?: string) => void;
  clearIngredientSource: () => void;
  cancelRecipeGeneration: () => void;
  hideRecipeGenerationModal: () => void;
  showRecipeResult: (recipe: any, generationOptions?: any) => void;
  hideRecipeResult: () => void;
  showSavedRecipe: (recipe: Recipe) => Promise<void>;
  
  // Meal actions
  addMeal: (meal: {
    mealName: string;
    imageUrl: string;
    servingSize?: string;
    confidence: number;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
    sodium: number;
    ingredients: string[];
    aiSuggestions: string[];
  }) => Promise<void>;
  getMealById: (id: string) => UserMeal | null;
  deleteMeal: (id: string) => Promise<void>;
  
  // Daily Nutrition Tracking Actions
  logMealToNutrition: (meal: UserMeal) => Promise<boolean>;
  cleanupOrphanedNutritionData: () => Promise<{ cleaned: number; recalculated: string[] }>;
  logRecipeToNutrition: (recipe: Recipe, servings?: number) => Promise<boolean>;
  
  // Meal Generation Preferences actions
  getMealGenerationPreferences: () => Promise<any>;
  saveMealGenerationPreferences: (preferences: any) => Promise<void>;
  getCompleteMealGenerationContext: () => Promise<any>;
  generateRecipeWithAI: (options: any) => Promise<any>;
  generateRecipeFromPhoto: (detectedIngredients: string[], options: any) => Promise<any>;
  
  // Body Stats & Goals Actions
  updateBodyStats: (stats: {
    age?: number;
    gender?: 'male' | 'female' | 'other';
    height?: number;
    weight?: number;
    targetWeight?: number;
    goal?: 'lose' | 'maintain' | 'gain';
    activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  }) => Promise<void>;
  calculateDailyCalorieTarget: () => number | null;
  
  // Nutrition Update Event Actions
  triggerNutritionUpdate: () => void;
  
  // Meal Planning Actions
  loadMealPlan: (startDate: string, endDate: string) => Promise<any>;
  addMealToWeek: (date: string, mealType: 'breakfast' | 'lunch' | 'dinner', recipe: Recipe) => Promise<boolean>;
  removeMealFromWeek: (date: string, mealType: 'breakfast' | 'lunch' | 'dinner') => Promise<boolean>;
  
  shoppingLists: ShoppingList[];
  loadShoppingLists: () => Promise<void>;
  createShoppingList: (shoppingList: {
    title: string;
    notes?: string;
    items: Array<{
      name: string;
      quantity: number;
      unit?: string;
      category?: string;
    }>;
  }) => Promise<ShoppingList | null>;
  addShoppingListItem: (item: {
    name: string;
    quantity: number;
    unit?: string;
    category?: string;
    listId?: string;
  }) => Promise<ShoppingListItem | null>;
  updateShoppingListItem: (itemId: string, updates: {
    isPurchased?: boolean;
    quantity?: number;
    notes?: string;
  }) => Promise<boolean>;
  updateShoppingListItemName: (itemId: string, newName: string) => Promise<boolean>;
  toggleShoppingListItem: (itemId: string) => Promise<boolean>;
  deleteShoppingListItem: (itemId: string) => Promise<boolean>;
  deleteShoppingList: (listId: string) => Promise<boolean>;

  // Onboarding State
  onboarding: {
    isFirstTimeUser: boolean;
    showOnboarding: boolean;
    hasCompletedOnboarding: boolean;
  };

  // Onboarding Actions
  checkFirstTimeUser: () => void;
  setOnboardingComplete: () => void;
  hideOnboarding: () => void;
};

// Helper function to detect measurement system based on locale/IP
const detectMeasurementSystem = (): 'metric' | 'imperial' => {
  try {
    // Check browser locale first
    const locale = navigator.language;
    
    // Imperial countries: USA, Liberia, Myanmar/Burma
    const imperialCountries = ['en-US', 'en-LR', 'my-MM'];
    
    if (imperialCountries.some(country => locale.startsWith(country.split('-')[1]))) {
      return 'imperial';
    }
    
    // Default to metric for all other countries
    return 'metric';
  } catch (error) {
    // Fallback to metric if detection fails
    return 'metric';
  }
};

// Default user profile for fallback
const defaultUser: UserProfile = {
  name: 'User',
  email: '',
  avatar: '/img/avatars/avatar-1.png',
  points: 0,
  level: 1,
  preferences: [],
  allergies: [],
  plan: 'free',
  apiProvider: 'openai',
  measurementSystem: detectMeasurementSystem(),
  floatingBarTheme: 'light',
  isAdmin: false,
};

// Manual theme persistence functions
const getStoredTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('nosh-theme');
    // Getting stored theme
    return (stored as 'light' | 'dark') || 'light';
  }
  return 'light';
};

const saveTheme = (theme: 'light' | 'dark') => {
  if (typeof window !== 'undefined') {
    console.log('Saving theme to localStorage:', theme);
    localStorage.setItem('nosh-theme', theme);
  }
};

export const useNoshStore = create<NoshState>()(
  persist(
    (set, get) => ({
      theme: getStoredTheme(), // Use manual storage for theme
      inventory: [],
      recipes: [],
      expiringItems: [],
      meals: [],
      user: defaultUser,
      loading: {
        inventory: false,
        recipes: false,
        user: false,
        expiringItems: false,
        meals: false,
      },
      dataFreshness: {
        inventory: 0,
        recipes: 0,
        user: 0,
        expiringItems: 0,
        meals: 0,
      },
      isOnline: false,
      
      // Nutrition Update Event System
      nutritionUpdateTrigger: 0,

      // Global Image Recognition State
      imageRecognition: {
        isActive: false,
        showModal: false,
        currentPhase: 'connecting',
        elapsedTime: 0,
        estimatedTime: 60,
        startTime: 0,
        scanType: 'inventory',
        error: null,
      },

      // Global AI Usage Limit Modal State
      usageLimitModal: {
        isOpen: false,
        limits: null,
        featureType: null,
      },

      // Global Nutrition Analysis State
      nutritionAnalysis: {
        isActive: false,
        showModal: false,
        mealData: null,
        imageUrl: '',
        imagePath: '',
        timestamp: '',
        error: null,
        mode: 'analysis',
      },

      // Global Scan Results State
      scanResults: {
        isActive: false,
        showModal: false,
        scanData: null,
        imageUrl: '',
        imagePath: '',
        timestamp: '',
        error: null,
      },

          // Global Recipe Generation State
    recipeGeneration: {
      isActive: false,
      showModal: false,
      showResultModal: false,
      currentPhase: 'initializing',
      elapsedTime: 0,
      estimatedTime: 120,
      startTime: 0,
      error: null,
      generatedRecipe: null,
      generationOptions: null,
      hasAnimated: false,
      // NEW: Image Generation State (Pure Addition)
      imageGeneration: {
        isGenerating: false,
        imageUrl: null,
        error: null,
      },
    },
    
    // Ingredient Source Selection State
    ingredientSource: {
      showModal: false,
      selectedSource: null,
      detectedIngredients: [],
      detectionId: null,
    },

      // Data freshness utility
      isDataFresh: (dataType, maxAgeMinutes = 5) => {
        const lastLoaded = get().dataFreshness[dataType];
        if (lastLoaded === 0) return false; // Never loaded
        const now = Date.now();
        const maxAgeMs = maxAgeMinutes * 60 * 1000;
        return (now - lastLoaded) < maxAgeMs;
      },

      // Data loading actions
      loadInventory: async (force = false) => {
        // Check if data is fresh and don't reload unless forced
        if (!force && get().isDataFresh('inventory')) {
          return;
        }

        try {
          set((state) => ({ 
            loading: { ...state.loading, inventory: true } 
          }));
          
          // Add PWA debugging
          const { isPWAStandalone, getPWAInfo } = await import('./utils');
          const pwaInfo = getPWAInfo();
          
          if (pwaInfo.isStandalone) {
            console.log('🔍 PWA Mode Detected - Loading Inventory:', pwaInfo);
          }
          
          const inventory = await db.getFoodItems();
          
          if (pwaInfo.isStandalone) {
            console.log(`📦 PWA Inventory Load Result: ${inventory.length} items`);
            if (inventory.length === 0) {
              console.warn('⚠️ No inventory items loaded in PWA mode - check authentication');
            }
          }
          
          // Only update inventory if we got data or if current inventory is empty
          // This prevents clearing data when auth fails
          if (inventory.length > 0 || get().inventory.length === 0) {
            set((state) => ({ 
              inventory,
              dataFreshness: { ...state.dataFreshness, inventory: Date.now() }
            }));
          } else {
            console.log('No inventory data received, keeping existing data');
          }
        } catch (error) {
          console.error('Failed to load inventory:', error);
          
          // Add extra PWA error logging
          const { getPWAInfo } = await import('./utils');
          const pwaInfo = getPWAInfo();
          if (pwaInfo.isStandalone) {
            console.error('🚨 PWA Inventory Load Error:', {
              error: error instanceof Error ? error.message : String(error),
              pwaInfo,
              timestamp: new Date().toISOString()
            });
          }
        } finally {
          set((state) => ({ 
            loading: { ...state.loading, inventory: false } 
          }));
        }
      },

      loadRecipes: async (force = false) => {
        // Check if data is fresh and don't reload unless forced
        if (!force && get().isDataFresh('recipes')) {
          return;
        }

        try {
          set((state) => ({ 
            loading: { ...state.loading, recipes: true } 
          }));
          
          const recipes = await db.getRecipes();
          
          // Only update recipes if we got data or if current recipes is empty
          if (recipes.length > 0 || get().recipes.length === 0) {
            set((state) => ({ 
              recipes,
              dataFreshness: { ...state.dataFreshness, recipes: Date.now() }
            }));
          } else {
            console.log('No recipes data received, keeping existing data');
          }
        } catch (error) {
          console.error('Failed to load recipes:', error);
        } finally {
          set((state) => ({ 
            loading: { ...state.loading, recipes: false } 
          }));
        }
      },

      loadUserProfile: async (force = false) => {
        // User profile has shorter cache time (2 minutes) for updates
        if (!force && get().isDataFresh('user', 2)) {
          return;
        }

        try {
          set((state) => ({ 
            loading: { ...state.loading, user: true } 
          }));
          
          // First migrate any old custom allergies data
          await db.migrateCustomAllergiesData();
          
          const profile = await db.getUserProfile();
          
          if (profile) {
            const user: UserProfile = {
              name: profile.name || 'User',
              email: profile.email || '',
              avatar: profile.avatar_url || defaultUser.avatar,
              points: profile.points,
              level: profile.level,
              preferences: Array.isArray(profile.preferences?.dietary) ? profile.preferences.dietary : 
                          Array.isArray(profile.preferences) ? profile.preferences : [], // Backward compatibility
              allergies: Array.isArray(profile.preferences?.allergies) ? profile.preferences.allergies : [],
              plan: 'free', // Default plan since it's not in the database yet
              apiProvider: (profile.api_provider === 'openai' || profile.api_provider === 'openrouter') 
                ? profile.api_provider 
                : 'openai', // Validate and provide fallback
              measurementSystem: (profile.preferences?.measurementSystem === 'metric' || profile.preferences?.measurementSystem === 'imperial')
                ? profile.preferences.measurementSystem
                : detectMeasurementSystem(), // Smart default based on locale
              floatingBarTheme: (profile.preferences?.floatingBarTheme === 'light' || profile.preferences?.floatingBarTheme === 'dark')
                ? profile.preferences.floatingBarTheme
                : 'light', // Default to light theme
              isAdmin: profile.is_admin || false, // Load admin status from database
              // Body Stats & Goals
              age: profile.age || undefined,
              gender: profile.gender || undefined,
              height: profile.height || undefined,
              weight: profile.weight || undefined,
              targetWeight: profile.target_weight || undefined,
              goal: profile.goal || undefined,
              activityLevel: profile.activity_level || undefined,
              dailyCalorieTarget: profile.daily_calorie_target || undefined,
            };
            set((state) => ({ 
              user,
              dataFreshness: { ...state.dataFreshness, user: Date.now() }
            }));
            // User profile loaded successfully
            
            // Check if this is a first-time user after profile is loaded
            setTimeout(() => {
              get().checkFirstTimeUser();
            }, 500); // Small delay to ensure UI is ready
          }
        } catch (error) {
          console.error('Failed to load user profile:', error);
        } finally {
          set((state) => ({ 
            loading: { ...state.loading, user: false } 
          }));
        }
      },

      loadExpiringItems: async (force = false) => {
        if (!force && get().isDataFresh('expiringItems')) {
          return;
        }

        try {
          set((state) => ({ 
            loading: { ...state.loading, expiringItems: true } 
          }));
          
          const expiringItems = await db.getExpiringItems();
          
          // Only update expiring items if we got data or if current list is empty
          if (expiringItems.length > 0 || get().expiringItems.length === 0) {
            set((state) => ({ 
              expiringItems,
              dataFreshness: { ...state.dataFreshness, expiringItems: Date.now() }
            }));
          } else {
            // No expiring items data received, keeping existing data
          }
        } catch (error) {
          console.error('Failed to load expiring items:', error);
        } finally {
          set((state) => ({ 
            loading: { ...state.loading, expiringItems: false } 
          }));
        }
      },

      loadMeals: async (force = false) => {
        if (!force && get().isDataFresh('meals')) {
          return;
        }

        try {
          set((state) => ({ 
            loading: { ...state.loading, meals: true } 
          }));
          
          const meals = await db.getUserMeals();
          
          // Only update meals if we got data or if current meals is empty
          if (meals.length > 0 || get().meals.length === 0) {
            set((state) => ({ 
              meals,
              dataFreshness: { ...state.dataFreshness, meals: Date.now() }
            }));
          } else {
            // No meals data received, keeping existing data
          }
        } catch (error) {
          console.error('Failed to load meals:', error);
        } finally {
          set((state) => ({ 
            loading: { ...state.loading, meals: false } 
          }));
        }
      },

      loadAllData: async (force = false) => {
        const { loadInventory, loadRecipes, loadUserProfile, loadExpiringItems, loadMeals, checkOnlineStatus } = get();
        
        await checkOnlineStatus();
        
        // Load all data in parallel
        await Promise.all([
          loadInventory(force),
          loadRecipes(force),
          loadUserProfile(force),
          loadExpiringItems(force),
          loadMeals(force),
        ]);
      },

      checkOnlineStatus: async () => {
        try {
          const isOnline = await db.isOnline();
          set({ isOnline });
        } catch (error) {
          console.error('Failed to check online status:', error);
          set({ isOnline: false });
        }
      },

      // CRUD actions
      addItem: async (item) => {
        try {
          const newItem = await db.addFoodItem(item);
          if (newItem) {
            set((state) => ({ 
              inventory: [...state.inventory, newItem] 
            }));
            
            // Reload expiring items to update the list
            get().loadExpiringItems();
          }
        } catch (error) {
          console.error('Failed to add item:', error);
        }
      },

      removeItem: async (id) => {
        try {
          const success = await db.deleteFoodItem(id);
          if (success) {
            set((state) => ({ 
              inventory: state.inventory.filter(item => item.id !== id),
              expiringItems: state.expiringItems.filter(item => item.id !== id)
            }));
          }
        } catch (error) {
          console.error('Failed to remove item:', error);
        }
      },

      updateItem: async (id, updatedItem) => {
        try {
          const updated = await db.updateFoodItem(id, updatedItem);
          if (updated) {
            set((state) => ({
              inventory: state.inventory.map(item => 
                item.id === id ? updated : item
              ),
              expiringItems: state.expiringItems.map(item => 
                item.id === id ? updated : item
              )
            }));
            
            // Reload expiring items to update the list
            get().loadExpiringItems();
          }
        } catch (error) {
          console.error('Failed to update item:', error);
        }
      },

      toggleFavoriteRecipe: async (id) => {
        try {
          const success = await db.toggleRecipeFavorite(id);
          if (success) {
            set((state) => ({
              recipes: state.recipes.map(recipe => 
                recipe.id === id ? { ...recipe, isFavorite: !recipe.isFavorite } : recipe
              )
            }));
          }
        } catch (error) {
          console.error('Failed to toggle recipe favorite:', error);
        }
      },

      updateUserPoints: async (points) => {
        try {
          const success = await db.updateUserPoints(points);
          if (success) {
            set((state) => ({
              user: {
                ...state.user,
                points: state.user.points + points,
                level: Math.floor((state.user.points + points) / 300) + 1
              }
            }));
          }
        } catch (error) {
          console.error('Failed to update user points:', error);
        }
      },

      updateUserAvatar: async (avatarUrl) => {
        try {
          const success = await db.updateUserAvatar(avatarUrl);
          
          if (success) {
            set((state) => ({
              user: {
                ...state.user,
                avatar: avatarUrl
              },
              // Invalidate user profile cache to force fresh load on next access
              dataFreshness: { ...state.dataFreshness, user: 0 }
            }));
          } else {
            throw new Error('Database update returned false');
          }
        } catch (error) {
          console.error('Failed to update user avatar:', error);
          throw error; // Re-throw so the UI can handle it
        }
      },

      updateUserApiProvider: (provider) => {
        set((state) => ({
          user: {
            ...state.user,
            apiProvider: provider
          }
        }));
        
        // Save to database
        db.updateUserApiProvider(provider).catch(error => {
          console.error('Failed to save API provider preference:', error);
        });
      },

      updateUserMeasurementSystem: async (system) => {
        try {
          const success = await db.updateUserMeasurementSystem(system);
          if (success) {
            set((state) => ({
              user: {
                ...state.user,
                measurementSystem: system
              }
            }));
          }
        } catch (error) {
          console.error('Failed to update measurement system preference:', error);
        }
      },

      updateFloatingBarTheme: async (theme) => {
        try {
          const success = await db.updateFloatingBarTheme(theme);
          if (success) {
            set((state) => ({
              user: {
                ...state.user,
                floatingBarTheme: theme
              }
            }));
          }
        } catch (error) {
          console.error('Failed to update floating bar theme preference:', error);
        }
      },

      updateUserPreferences: async (preferences) => {
        try {
          const success = await db.updateUserPreferences(preferences);
          if (success) {
            set((state) => ({
              user: {
                ...state.user,
                preferences: preferences
              }
            }));
          }
        } catch (error) {
          console.error('Failed to update user preferences:', error);
        }
      },

      updateUserAllergies: async (allergies) => {
        try {
          const success = await db.updateUserAllergies(allergies);
          if (success) {
            set((state) => ({
              user: {
                ...state.user,
                allergies: allergies
              }
            }));
          }
        } catch (error) {
          console.error('Failed to update user allergies:', error);
        }
      },

      updateUserName: async (name) => {
        try {
          const success = await db.updateUserName(name);
          
          if (success) {
            set((state) => ({
              user: {
                ...state.user,
                name: name
              }
            }));
          } else {
            throw new Error('Database update returned false');
          }
        } catch (error) {
          console.error('Failed to update user name:', error);
          throw error; // Re-throw so the UI can handle it
        }
      },

      migrateUserData: async () => {
        try {
          const success = await db.migrateCustomAllergiesData();
          if (success) {
            // Reload user profile to get the updated data
            await get().loadUserProfile();
          }
          return success;
        } catch (error) {
          console.error('Failed to migrate user data:', error);
          return false;
        }
      },

      resetUserProfile: () => {
        set({ 
          user: defaultUser,
          // Reset onboarding state for new users
          onboarding: {
            isFirstTimeUser: false,
            showOnboarding: false,
            hasCompletedOnboarding: false,
          }
        });
        
        // Clear persisted store data to ensure clean state for new users
        try {
          localStorage.removeItem('nosh-store');
          console.log('🧹 Cleared persisted store data for clean user state');
        } catch (error) {
          console.warn('Failed to clear persisted store data:', error);
        }
      },

      setTheme: (theme) => {
        console.log('setTheme called with:', theme);
        set({ theme });
        console.log('Theme set in store');
        saveTheme(theme);
      },
      toggleTheme: () => {
        console.log('toggleTheme called');
        set((state) => {
          const newTheme = state.theme === 'light' ? 'dark' : 'light';
          console.log('Toggling theme from', state.theme, 'to', newTheme);
          saveTheme(newTheme);
          return { theme: newTheme };
        });
      },

      // Global Image Recognition Actions
      startImageRecognition: (scanType) => {
        // Clear any existing interval
        const currentState = get();
        if (currentState.imageRecognition.intervalId) {
          clearInterval(currentState.imageRecognition.intervalId);
        }

        // Start progress tracking
        const startTime = Date.now();
        const intervalId = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          set((state) => ({
            imageRecognition: {
              ...state.imageRecognition,
              elapsedTime: elapsed
            }
          }));
        }, 1000);

        set((state) => ({
          imageRecognition: {
            ...state.imageRecognition,
            isActive: true,
            showModal: true,
            currentPhase: 'connecting',
            elapsedTime: 0,
            estimatedTime: scanType === 'meal' ? 90 : 60,
            startTime,
            scanType,
            error: null,
            intervalId
          }
        }));
      },

      updateImageRecognitionProgress: (phase, elapsed) => {
        set((state) => ({
          imageRecognition: {
            ...state.imageRecognition,
            currentPhase: phase,
            elapsedTime: elapsed
          }
        }));
      },

      setImageRecognitionError: (error) => {
        const currentState = get();
        if (currentState.imageRecognition.intervalId) {
          clearInterval(currentState.imageRecognition.intervalId);
        }

        set((state) => ({
          imageRecognition: {
            ...state.imageRecognition,
            isActive: false,
            error,
            intervalId: undefined
          }
        }));
      },

      completeImageRecognition: () => {
        const currentState = get();
        if (currentState.imageRecognition.intervalId) {
          clearInterval(currentState.imageRecognition.intervalId);
        }

        set((state) => ({
          imageRecognition: {
            ...state.imageRecognition,
            isActive: false,
            showModal: false,
            currentPhase: 'connecting',
            elapsedTime: 0,
            startTime: 0,
            error: null,
            intervalId: undefined,
            imagePath: undefined
          }
        }));
      },

      cancelImageRecognition: () => {
        const currentState = get();
        if (currentState.imageRecognition.intervalId) {
          clearInterval(currentState.imageRecognition.intervalId);
        }

        set((state) => ({
          imageRecognition: {
            ...state.imageRecognition,
            isActive: false,
            showModal: false,
            currentPhase: 'connecting',
            elapsedTime: 0,
            startTime: 0,
            error: null,
            intervalId: undefined,
            imagePath: undefined
          }
        }));
      },

      hideImageRecognitionModal: () => {
        set((state) => ({
          imageRecognition: {
            ...state.imageRecognition,
            showModal: false
          }
        }));
      },

      setImageRecognitionPath: (path) => {
        set((state) => ({
          imageRecognition: {
            ...state.imageRecognition,
            imagePath: path
          }
        }));
      },

      // Global AI Usage Limit Modal Actions
      showUsageLimitModal: (limits, featureType) => {
        console.log('🚫 [STORE] Showing global usage limit modal:', { featureType, limits });
        set((state) => ({
          usageLimitModal: {
            isOpen: true,
            limits,
            featureType
          }
        }));
      },

      hideUsageLimitModal: () => {
        console.log('✅ [STORE] Hiding global usage limit modal');
        set((state) => ({
          usageLimitModal: {
            isOpen: false,
            limits: null,
            featureType: null
          }
        }));
      },

      // Global Nutrition Analysis Actions
      showNutritionAnalysis: (data) => {
        set((state) => ({
          nutritionAnalysis: {
            ...state.nutritionAnalysis,
            isActive: true,
            showModal: true,
            mealData: data.mealData,
            imageUrl: data.imageUrl,
            imagePath: data.imagePath,
            timestamp: new Date().toISOString(),
            error: null,
            mode: data.mode || 'analysis'
          }
        }));
      },

      hideNutritionAnalysis: () => {
        set((state) => ({
          nutritionAnalysis: {
            ...state.nutritionAnalysis,
            isActive: false,
            showModal: false
          }
        }));
      },

      setNutritionAnalysisError: (error) => {
        set((state) => ({
          nutritionAnalysis: {
            ...state.nutritionAnalysis,
            error
          }
        }));
      },

      clearNutritionAnalysis: () => {
        set((state) => ({
          nutritionAnalysis: {
            ...state.nutritionAnalysis,
            isActive: false,
            showModal: false,
            mealData: null,
            imageUrl: '',
            imagePath: '',
            timestamp: '',
            error: null,
            mode: 'analysis'
          }
        }));
      },

      // Global Scan Results Actions
      showScanResults: (data) => {
        set((state) => ({
          scanResults: {
            ...state.scanResults,
            isActive: true,
            showModal: true,
            scanData: data.scanData,
            imageUrl: data.imageUrl,
            imagePath: data.imagePath,
            timestamp: new Date().toISOString(),
            error: null
          }
        }));
      },

      hideScanResults: () => {
        set((state) => ({
          scanResults: {
            ...state.scanResults,
            isActive: false,
            showModal: false
          }
        }));
      },

      setScanResultsError: (error) => {
        set((state) => ({
          scanResults: {
            ...state.scanResults,
            error
          }
        }));
      },

      clearScanResults: () => {
        set((state) => ({
          scanResults: {
            ...state.scanResults,
            isActive: false,
            showModal: false,
            scanData: null,
            imageUrl: '',
            imagePath: '',
            timestamp: '',
            error: null
          }
        }));
      },

      // Global Recipe Generation Actions
      startRecipeGeneration: () => {
        // Clear any existing interval
        const currentState = get();
        if (currentState.recipeGeneration.intervalId) {
          clearInterval(currentState.recipeGeneration.intervalId);
        }

        // Start progress tracking
        const startTime = Date.now();
        const intervalId = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          set((state) => ({
            recipeGeneration: {
              ...state.recipeGeneration,
              elapsedTime: elapsed
            }
          }));
        }, 1000);

        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            isActive: true,
            showModal: true,
            showResultModal: false,
            currentPhase: 'initializing',
            elapsedTime: 0,
            estimatedTime: 120,
            startTime,
            error: null,
            generatedRecipe: null,
            hasAnimated: false,
            intervalId
          }
        }));

        // Set hasAnimated to true after animation completes
        setTimeout(() => {
          set((state) => ({
            recipeGeneration: {
              ...state.recipeGeneration,
              hasAnimated: true
            }
          }));
        }, 800);
      },

      updateRecipeGenerationProgress: (phase, elapsed) => {
        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            currentPhase: phase,
            elapsedTime: elapsed
          }
        }));
      },

      setRecipeGenerationError: (error) => {
        const currentState = get();
        if (currentState.recipeGeneration.intervalId) {
          clearInterval(currentState.recipeGeneration.intervalId);
        }

        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            isActive: false,
            error,
            intervalId: undefined
          }
        }));
      },

      completeRecipeGeneration: (recipe) => {
        const currentState = get();
        if (currentState.recipeGeneration.intervalId) {
          clearInterval(currentState.recipeGeneration.intervalId);
        }

        // IMMEDIATE UI UPDATE: Add recipe directly to store for instant UI response
        if (recipe && !currentState.recipes.find(r => r.id === recipe.id)) {
          set((state) => ({
            recipes: [...state.recipes, {
              id: recipe.id,
              title: recipe.name,
              description: recipe.description,
              ingredients: recipe.ingredients || [],
              time: recipe.cookTime || recipe.time || 30,
              prepTime: recipe.prepTime,
              servings: recipe.servings || 1,
              calories: recipe.calories || 0,
              nutrition: recipe.nutrition,
              difficulty: recipe.difficulty || 'medium',
              mealType: recipe.mealType,
              cuisineStyle: recipe.cuisineStyle,
              spiceLevel: recipe.spiceLevel,
              estimatedCost: recipe.estimatedCost,
              tags: recipe.tags || [],
              isFavorite: true, // Auto-saved recipes are automatically favorited
              imageUrl: recipe.imageUrl || recipe.image || '', // Don't set placeholder here - let components handle fallback
              createdAt: new Date().toISOString() // Add current timestamp for newly generated recipes
            }],
            recipeGeneration: {
              ...state.recipeGeneration,
              isActive: false,
              showModal: false,
              showResultModal: true,
              currentPhase: 'complete',
              generatedRecipe: recipe,
              intervalId: undefined
            }
          }));
        } else {
          set((state) => ({
            recipeGeneration: {
              ...state.recipeGeneration,
              isActive: false,
              showModal: false,
              showResultModal: true,
              currentPhase: 'complete',
              generatedRecipe: recipe,
              intervalId: undefined
            }
          }));
        }

        // BACKGROUND DATABASE SYNC: Refresh from database to ensure consistency
        setTimeout(async () => {
          try {
            const initialRecipeCount = get().recipes.length;
            
            // Wait for database transaction to fully commit
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Multiple refresh attempts with exponential backoff
            let attempts = 0;
            const maxAttempts = 3;
            let newRecipeCount = initialRecipeCount;
            
                         while (attempts < maxAttempts) {
               await get().loadRecipes(true); // Force refresh bypassing cache
               newRecipeCount = get().recipes.length;
               
               // Check if the specific recipe we generated is now in the database
               const recipeExistsInDatabase = get().recipes.some(r => r.id === recipe.id);
               
               if (recipeExistsInDatabase) {
                 break;
               }
               
               attempts++;
               if (attempts < maxAttempts) {
                 // Exponential backoff: 500ms, 1000ms, 1500ms
                 const delay = 500 * attempts;
                 await new Promise(resolve => setTimeout(resolve, delay));
               }
             }
             
             // Final verification: Check if our specific recipe exists in the database
             const finalRecipeCheck = get().recipes.some(r => r.id === recipe.id);
             if (!finalRecipeCheck) {
               console.warn('Auto-refresh: Generated recipe not found in database after sync attempts');
             }
          } catch (error) {
            console.error('Auto-refresh failed:', error);
          }
        }, 100); // Start background sync after 100ms
      },

      cancelRecipeGeneration: () => {
        const currentState = get();
        if (currentState.recipeGeneration.intervalId) {
          clearInterval(currentState.recipeGeneration.intervalId);
        }

        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            isActive: false,
            showModal: false,
            showResultModal: false,
            currentPhase: 'initializing',
            elapsedTime: 0,
            startTime: 0,
            error: null,
            generatedRecipe: null,
            generationOptions: null,
            hasAnimated: false,
            intervalId: undefined
          }
        }));
      },

      hideRecipeGenerationModal: () => {
        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            showModal: false
          }
        }));
      },

      showRecipeResult: (recipe, generationOptions) => {
        const currentState = get();
        
        // IMMEDIATE UI UPDATE: Add recipe directly to store for instant UI response
        if (recipe && !currentState.recipes.find(r => r.id === recipe.id)) {
          set((state) => ({
            recipes: [...state.recipes, {
              id: recipe.id,
              title: recipe.name,
              description: recipe.description,
              ingredients: recipe.ingredients || [],
              time: recipe.cookTime || recipe.time || 30,
              prepTime: recipe.prepTime,
              servings: recipe.servings || 1,
              calories: recipe.calories || 0,
              nutrition: recipe.nutrition,
              difficulty: recipe.difficulty || 'medium',
              mealType: recipe.mealType,
              cuisineStyle: recipe.cuisineStyle,
              spiceLevel: recipe.spiceLevel,
              estimatedCost: recipe.estimatedCost,
              tags: recipe.tags || [],
              isFavorite: true, // Auto-saved recipes are automatically favorited
              imageUrl: recipe.imageUrl || recipe.image || '', // Don't set placeholder here - let components handle fallback
              createdAt: new Date().toISOString() // Add current timestamp for newly generated recipes
            }],
            recipeGeneration: {
              ...state.recipeGeneration,
              showModal: false,
              showResultModal: true,
              generatedRecipe: recipe,
              generationOptions: generationOptions || null,
              isActive: false,
              // Clear previous image state when showing new recipe
              imageGeneration: {
                isGenerating: false,
                imageUrl: null,
                error: null
              }
            }
          }));
        } else {
          set((state) => ({
            recipeGeneration: {
              ...state.recipeGeneration,
              showModal: false,
              showResultModal: true,
              generatedRecipe: recipe,
              generationOptions: generationOptions || null,
              isActive: false,
              // Clear previous image state when showing new recipe
              imageGeneration: {
                isGenerating: false,
                imageUrl: null,
                error: null
              }
            }
          }));
        }

        // BACKGROUND DATABASE SYNC: Refresh from database to ensure consistency
        setTimeout(async () => {
          try {
            const initialRecipeCount = get().recipes.length;
            
            // Wait for database transaction to fully commit
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Multiple refresh attempts with exponential backoff
            let attempts = 0;
            const maxAttempts = 3;
            let newRecipeCount = initialRecipeCount;
            
            while (attempts < maxAttempts) {
              await get().loadRecipes(true); // Force refresh bypassing cache
              newRecipeCount = get().recipes.length;
              
              // Check if the specific recipe we generated is now in the database
              const recipeExistsInDatabase = get().recipes.some(r => r.id === recipe.id);
              
              if (recipeExistsInDatabase) {
                break;
              }
              
              attempts++;
              if (attempts < maxAttempts) {
                // Exponential backoff: 500ms, 1000ms, 1500ms
                const delay = 500 * attempts;
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            }
            
            // Final verification: Check if our specific recipe exists in the database
            const finalRecipeCheck = get().recipes.some(r => r.id === recipe.id);
            if (!finalRecipeCheck) {
              console.warn('Auto-refresh: Generated recipe not found in database after sync attempts');
            }
          } catch (error) {
            console.error('Auto-refresh failed:', error);
          }
        }, 100); // Start background sync after 100ms
      },

      hideRecipeResult: () => {
        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            showResultModal: false,
            generatedRecipe: null,
            generationOptions: null,
            // Clear image generation state when hiding recipe
            imageGeneration: {
              isGenerating: false,
              imageUrl: null,
              error: null
            }
          }
        }));
      },

      // NEW: Recipe Image Generation Actions (Pure Addition - No existing code modified)
      generateRecipeImage: async (recipeData) => {
        const recipeName = recipeData?.name;
        console.log('🎨 [STORE] generateRecipeImage called for:', recipeName);
        
        // CRITICAL: Guard against duplicate calls for same recipe
        const currentState = get();
        if (currentState.recipeGeneration.imageGeneration?.isGenerating) {
          console.log('🚫 [STORE] Image generation already in progress - blocking duplicate call');
          return;
        }
        
        // Additional guard: check if we already have an image for this recipe
        if (currentState.recipeGeneration.imageGeneration?.imageUrl && 
            currentState.recipeGeneration.generatedRecipe?.name === recipeName) {
          console.log('🚫 [STORE] Image already exists for this recipe - blocking duplicate call');
          return;
        }
        
        console.log('🎨 [STORE] Recipe data received:', {
          hasName: !!recipeData?.name,
          hasDescription: !!recipeData?.description,
          hasIngredients: !!recipeData?.ingredients,
          ingredientCount: recipeData?.ingredients?.length,
          hasCuisineStyle: !!recipeData?.cuisineStyle,
          hasInstructions: !!recipeData?.instructions
        });
        
        console.log('🔍 [STORE] Current imageGeneration state before update:', currentState.recipeGeneration.imageGeneration);
        
        // Set generating state
        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            imageGeneration: {
              ...state.recipeGeneration.imageGeneration,
              isGenerating: true,
              error: null
            }
          }
        }));

        // Verify state was set
        const updatedState = get();
        console.log('✅ [STORE] Updated imageGeneration state:', updatedState.recipeGeneration.imageGeneration);

        try {
          console.log('🔄 [STORE] Calling db.generateRecipeImage...');
          const result = await db.generateRecipeImage(recipeData);
          console.log('📥 [STORE] Database response:', result);
          
          if (result.success && result.imageUrl) {
            console.log('✅ [STORE] Image generation successful:', {
              imageUrl: result.imageUrl.substring(0, 50) + '...',
              length: result.imageUrl.length
            });
                         set((state) => ({
               recipeGeneration: {
                 ...state.recipeGeneration,
                 imageGeneration: {
                   isGenerating: false,
                   imageUrl: result.imageUrl || null,
                   error: null
                 }
               }
             }));
          } else {
            console.error('❌ [STORE] Image generation failed:', result.error);
            set((state) => ({
              recipeGeneration: {
                ...state.recipeGeneration,
                imageGeneration: {
                  isGenerating: false,
                  imageUrl: null,
                  error: result.error || 'Image generation failed'
                }
              }
            }));
          }
        } catch (error) {
          console.error('❌ [STORE] Image generation error:', error);
          console.error('❌ [STORE] Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : 'No stack trace'
          });
          set((state) => ({
            recipeGeneration: {
              ...state.recipeGeneration,
              imageGeneration: {
                isGenerating: false,
                imageUrl: null,
                error: error instanceof Error ? error.message : 'Failed to generate image'
              }
            }
          }));
        }
      },

      setRecipeImageGenerating: (isGenerating) => {
        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            imageGeneration: {
              ...state.recipeGeneration.imageGeneration,
              isGenerating
            }
          }
        }));
      },

      setRecipeImageUrl: (imageUrl) => {
        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            imageGeneration: {
              ...state.recipeGeneration.imageGeneration,
              imageUrl: imageUrl || null,
              error: null
            }
          }
        }));
      },

      setRecipeImageError: (error) => {
        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            imageGeneration: {
              ...state.recipeGeneration.imageGeneration,
              error,
              isGenerating: false
            }
          }
        }));
      },

      clearRecipeImage: () => {
        set((state) => ({
          recipeGeneration: {
            ...state.recipeGeneration,
            imageGeneration: {
              isGenerating: false,
              imageUrl: null,
              error: null
            }
          }
        }));
      },

      // Ingredient Source Selection Actions
      showIngredientSourceModal: () => {
        set((state) => ({
          ingredientSource: {
            ...state.ingredientSource,
            showModal: true
          }
        }));
      },

      hideIngredientSourceModal: () => {
        set((state) => ({
          ingredientSource: {
            ...state.ingredientSource,
            showModal: false
          }
        }));
      },

      setSelectedIngredientSource: (source, detectedIngredients, detectionId) => {
        console.log('🔄 [Store] setSelectedIngredientSource called with:', {
          source,
          detectedIngredients,
          detectedIngredientsCount: detectedIngredients?.length || 0,
          detectedIngredientsType: typeof detectedIngredients,
          isArray: Array.isArray(detectedIngredients)
        });
        
        set((state) => ({
          ingredientSource: {
            ...state.ingredientSource,
            selectedSource: source,
            detectedIngredients: detectedIngredients || [],
            detectionId: detectionId || null
          }
        }));
        
        // Log the updated state
        const newState = get();
        console.log('✅ [Store] ingredientSource state updated:', {
          selectedSource: newState.ingredientSource.selectedSource,
          detectedIngredients: newState.ingredientSource.detectedIngredients,
          detectedIngredientsCount: newState.ingredientSource.detectedIngredients?.length || 0
        });
      },

      clearIngredientSource: () => {
        set((state) => ({
          ingredientSource: {
            ...state.ingredientSource,
            selectedSource: null,
            detectedIngredients: [],
            detectionId: null
          }
        }));
      },

      // Show saved recipe in the same modal as generated recipes
      showSavedRecipe: async (recipe: Recipe) => {
        console.log('🍳 [Store] showSavedRecipe called with:', recipe);
        
        try {
          // Fetch complete recipe data directly from database to get the full details
          console.log('🔄 [Store] Fetching complete recipe data from database...');
          const { supabase } = await import('../lib/supabase');
          const { data: fullRecipeData, error } = await supabase
            .from('recipes')
            .select('*')
            .eq('id', recipe.id)
            .single();

          if (error || !fullRecipeData) {
            console.error('❌ [Store] Failed to fetch complete recipe data:', error);
            throw new Error('Failed to load complete recipe data');
          }

          console.log('✅ [Store] Complete recipe data loaded:', fullRecipeData);
          console.log('🔍 [Store] Database description:', fullRecipeData.description);


          // Use the actual saved data from database
          const transformedRecipe = {
            id: fullRecipeData.id,
            name: fullRecipeData.title,
            title: fullRecipeData.title,
            description: fullRecipeData.description || `A delicious ${fullRecipeData.title.toLowerCase()} recipe with ${Array.isArray(fullRecipeData.ingredients) ? fullRecipeData.ingredients.length : 0} ingredients.`,
            image: fullRecipeData.image_url || recipe.imageUrl || '', // Use database image_url first, then fallback
            imageUrl: fullRecipeData.image_url || recipe.imageUrl || '', // Ensure both fields have the same value
            cookTime: fullRecipeData.cook_time || 30,
            prepTime: fullRecipeData.prep_time || 15,
            totalTime: (fullRecipeData.cook_time || 30) + (fullRecipeData.prep_time || 15),
            servings: fullRecipeData.servings || 2,
            difficulty: fullRecipeData.difficulty,
            calories: fullRecipeData.calories || recipe.calories,
            rating: 4.5,
            // Use the actual saved ingredients with their amounts and categories
            ingredients: Array.isArray(fullRecipeData.ingredients) 
              ? fullRecipeData.ingredients.map((ing: any) => ({
                  name: ing.name || ing,
                  amount: ing.amount || '1 serving',
                  category: ing.category || 'other',
                  fromInventory: ing.fromInventory || false
                }))
              : [],
            // Use the actual saved instructions
            instructions: Array.isArray(fullRecipeData.instructions)
              ? fullRecipeData.instructions.map((inst: any) => ({
                  step: inst.step || 1,
                  instruction: inst.instruction || inst
                }))
              : [
                  { step: 1, instruction: `Prepare all ingredients for ${fullRecipeData.title.toLowerCase()}.` },
                  { step: 2, instruction: 'Combine and cook ingredients according to your preferred method.' },
                  { step: 3, instruction: `Cook for approximately ${fullRecipeData.cook_time || 30} minutes.` },
                  { step: 4, instruction: 'Adjust seasoning to taste and serve hot.' }
                ],
            // Use the actual saved nutrition data
            nutrition: fullRecipeData.nutrition || {
              calories: fullRecipeData.calories || recipe.calories,
              protein: Math.round((fullRecipeData.calories || recipe.calories) * 0.25 / 4),
              carbs: Math.round((fullRecipeData.calories || recipe.calories) * 0.45 / 4),
              fat: Math.round((fullRecipeData.calories || recipe.calories) * 0.30 / 9),
              fiber: 8,
              sugar: 12,
              sodium: 800
            },
            // Use the actual saved tags - prioritize database tags over fallback
            tags: (Array.isArray(fullRecipeData.tags) && fullRecipeData.tags.length > 0) 
              ? fullRecipeData.tags 
              : (fullRecipeData.difficulty ? [fullRecipeData.difficulty, 'saved recipe'] : ['saved recipe']),
            mealType: fullRecipeData.meal_type || 'any',
            cuisineStyle: fullRecipeData.cuisine_style || 'international',
            spiceLevel: fullRecipeData.spice_level || 'medium',
            estimatedCost: fullRecipeData.estimated_cost || Math.round((fullRecipeData.calories || recipe.calories) * 0.015),
            // CRITICAL FIX: Include dietary_preferences from database
            dietary_preferences: fullRecipeData.dietary_preferences || [],
            user_allergies: fullRecipeData.user_allergies || [],
            isSavedRecipe: true
          };



          // Detect if this is a photo recipe based on saved tags
          const isPhotoRecipe = Array.isArray(fullRecipeData.tags) && 
            fullRecipeData.tags.some((tag: string) => 
              tag.toLowerCase().includes('photo') || 
              tag.toLowerCase().includes('detected')
            );

          // Create appropriate generation options for photo recipes
          const generationOptions = isPhotoRecipe ? {
            source: 'photo',
            type: 'view',
            detectedIngredients: transformedRecipe.ingredients.map((ing: any) => ing.name),
            constrainedToPhoto: true,
            isPhotoRecipe: true
          } : {
            source: 'saved',
            type: 'view'
          };

          console.log('🔍 [Store] Photo recipe detection:', { isPhotoRecipe, generationOptions });

          set((state) => ({
            recipeGeneration: {
              ...state.recipeGeneration,
              showResultModal: true,
              generatedRecipe: transformedRecipe,
              generationOptions: generationOptions,
              // Clear previous image state when showing saved recipe
              imageGeneration: {
                isGenerating: false,
                imageUrl: null,
                error: null
              }
            }
          }));
          
        } catch (error) {
          console.error('❌ [Store] Error showing saved recipe:', error);
          // Fallback to basic recipe display if database fetch fails
          set((state) => ({
            recipeGeneration: {
              ...state.recipeGeneration,
              showResultModal: true,
              generatedRecipe: {
                id: recipe.id,
                name: recipe.title,
                title: recipe.title,
                description: `A delicious ${recipe.title.toLowerCase()} recipe.`,
                image: recipe.imageUrl || '', // Use the recipe's imageUrl for consistency
                imageUrl: recipe.imageUrl || '', // Ensure both fields have the same value
                calories: recipe.calories,
                cookTime: recipe.time,
                difficulty: recipe.difficulty,
                ingredients: recipe.ingredients.map((ing: any) => ({
                  name: typeof ing === 'string' ? ing : ing.name,
                  amount: typeof ing === 'string' ? '1 serving' : ing.amount || '1 serving',
                  category: typeof ing === 'string' ? 'other' : ing.category || 'other',
                  fromInventory: typeof ing === 'string' ? false : ing.fromInventory || false
                })),
                instructions: [{ step: 1, instruction: 'Please check the original recipe for detailed instructions.' }],
                tags: ['saved recipe']
              },
              generationOptions: { source: 'saved', type: 'view' },
              // Clear previous image state when showing saved recipe (fallback)
              imageGeneration: {
                isGenerating: false,
                imageUrl: null,
                error: null
              }
            }
          }));
        }
      },

      // Meal actions
      addMeal: async (meal) => {
        try {
          const newMeal = await db.addUserMeal(meal);
          if (newMeal) {
            set((state) => ({ 
              meals: [...state.meals, newMeal] 
            }));
            
            // 🍽️ AUTO-LOG: Automatically log meal to daily nutrition tracking
            console.log('🍽️ Auto-logging meal to daily nutrition tracking...');
            const success = await get().logMealToNutrition(newMeal);
            if (success) {
              console.log('✅ Meal automatically logged to nutrition tracking');
              // 🔄 TRIGGER: Notify nutrition components to update
              get().triggerNutritionUpdate();
            } else {
              console.warn('⚠️ Failed to auto-log meal to nutrition tracking');
            }
          }
        } catch (error) {
          console.error('Failed to add meal:', error);
        }
      },

      getMealById: (id) => {
        return get().meals.find(meal => meal.id === id) || null;
      },

            deleteMeal: async (id) => {
        try {
          // First get the meal to find its nutrition entry
          const meal = get().meals.find(m => m.id === id);
          if (!meal) {
            console.error('❌ Meal not found for deletion:', id);
            return;
          }

          // Delete the meal from user_meals table
          const success = await db.deleteUserMeal(id);
          if (success) {
            // Remove from local state
            set((state) => ({
              meals: state.meals.filter(meal => meal.id !== id)
            }));
            
            // 🗑️ AUTO-REMOVE: Remove nutrition data from daily tracking
            console.log('🗑️ Auto-removing meal from daily nutrition tracking...');
            const nutritionRemoved = await db.removeNutritionEntryByMealId(id);
            if (nutritionRemoved) {
              console.log('✅ Meal nutrition data automatically removed from tracking');
              // 🔄 TRIGGER: Notify nutrition components to update
              get().triggerNutritionUpdate();
            } else {
              console.warn('⚠️ Failed to remove meal nutrition data from tracking');
            }
          }
        } catch (error) {
          console.error('Failed to delete meal:', error);
        }
      },

      // Daily Nutrition Tracking Methods
      logMealToNutrition: async (meal) => {
        try {
          const success = await db.addNutritionEntry({
            entryType: 'meal',
            entryId: meal.id,
            entryName: meal.mealName,
            calories: meal.calories,
            protein: meal.protein,
            carbs: meal.carbs,
            fat: meal.fat,
            fiber: meal.fiber,
            sugar: meal.sugar,
            sodium: meal.sodium,
            servings: 1.0,
            servingSize: meal.servingSize
          });
          
          if (success) {
            console.log('✅ Successfully logged meal to daily nutrition tracking');
            return true;
          } else {
            console.error('❌ Failed to log meal to daily nutrition tracking');
            return false;
          }
        } catch (error) {
          console.error('❌ Exception logging meal to nutrition tracking:', error);
          return false;
        }
      },

      cleanupOrphanedNutritionData: async () => {
        try {
          const result = await db.cleanupOrphanedNutritionEntries();
          if (result.cleaned > 0) {
            console.log(`🧹 Cleaned up ${result.cleaned} orphaned nutrition entries for ${result.recalculated.length} dates`);
          }
          return result;
        } catch (error) {
          console.error('❌ Exception cleaning up orphaned nutrition data:', error);
          return { cleaned: 0, recalculated: [] };
        }
      },

      logRecipeToNutrition: async (recipe, servings = 1) => {
        try {
          // Calculate per-serving nutrition values
          const perServingCalories = Math.round((recipe.calories || 0) / (recipe.servings || 1) * servings);
          const perServingProtein = parseFloat(((recipe.nutrition?.protein || 0) / (recipe.servings || 1) * servings).toFixed(2));
          const perServingCarbs = parseFloat(((recipe.nutrition?.carbs || 0) / (recipe.servings || 1) * servings).toFixed(2));
          const perServingFat = parseFloat(((recipe.nutrition?.fat || 0) / (recipe.servings || 1) * servings).toFixed(2));
          const perServingFiber = parseFloat(((recipe.nutrition?.fiber || 0) / (recipe.servings || 1) * servings).toFixed(2));
          const perServingSugar = parseFloat(((recipe.nutrition?.sugar || 0) / (recipe.servings || 1) * servings).toFixed(2));
          const perServingSodium = parseFloat(((recipe.nutrition?.sodium || 0) / (recipe.servings || 1) * servings).toFixed(2));
          
          const success = await db.addNutritionEntry({
            entryType: 'recipe',
            entryId: recipe.id,
            entryName: recipe.title,
            calories: perServingCalories,
            protein: perServingProtein,
            carbs: perServingCarbs,
            fat: perServingFat,
            fiber: perServingFiber,
            sugar: perServingSugar,
            sodium: perServingSodium,
            servings: servings,
            servingSize: `${servings} serving${servings !== 1 ? 's' : ''}`
          });
          
          if (success) {
            console.log('✅ Successfully logged recipe to daily nutrition tracking');
            // 🔄 TRIGGER: Notify nutrition components to update
            get().triggerNutritionUpdate();
            return true;
          } else {
            console.error('❌ Failed to log recipe to daily nutrition tracking');
            return false;
          }
        } catch (error) {
          console.error('❌ Exception logging recipe to nutrition tracking:', error);
          return false;
        }
      },

  // Meal Generation Preferences actions
  getMealGenerationPreferences: async () => {
    try {
      return await db.getMealGenerationPreferences();
    } catch (error) {
      console.error('Failed to get meal generation preferences:', error);
      return null;
    }
  },

  saveMealGenerationPreferences: async (preferences: any) => {
    try {
      const success = await db.saveMealGenerationPreferences(preferences);
      if (!success) {
        throw new Error('Failed to save meal generation preferences');
      }
    } catch (error) {
      console.error('Failed to save meal generation preferences:', error);
      throw error;
    }
  },

  getCompleteMealGenerationContext: async () => {
    try {
      return await db.getCompleteMealGenerationContext();
    } catch (error) {
      console.error('Failed to get complete meal generation context:', error);
      return {
        preferences: null,
        inventory: [],
        userProfile: null,
        userPreferences: [],
        userAllergies: [],
        shoppingItems: []
      };
    }
  },

  generateRecipeWithAI: async (options: any) => {
    console.log('🏪 [STORE] generateRecipeWithAI called with options:', JSON.stringify(options, null, 2));
    
    const { updateRecipeGenerationProgress } = get();
    
    try {
      console.log('🔄 [STORE] Calling db.generateRecipeWithAI...');
      const result = await db.generateRecipeWithAI(options, updateRecipeGenerationProgress);
      console.log('📥 [STORE] db.generateRecipeWithAI result:', result);
      return result;
    } catch (error) {
      console.error('❌ [STORE] Failed to generate recipe with AI:', error);
      console.error('❌ [STORE] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate recipe'
      };
    }
  },

  generateRecipeFromPhoto: async (detectedIngredients: string[], options: any) => {
    console.log('📸 [STORE] generateRecipeFromPhoto called with detected ingredients:', detectedIngredients);
    console.log('📸 [STORE] Options:', JSON.stringify(options, null, 2));
    
    const { updateRecipeGenerationProgress } = get();
    
    try {
      console.log('🔄 [STORE] Calling db.generateRecipeFromPhoto...');
      const result = await db.generateRecipeFromPhoto(detectedIngredients, options, updateRecipeGenerationProgress);
      console.log('📥 [STORE] db.generateRecipeFromPhoto result:', result);
      return result;
    } catch (error) {
      console.error('❌ [STORE] Failed to generate recipe from photo:', error);
      console.error('❌ [STORE] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate recipe from photo'
      };
    }
  },

  // Body Stats & Goals Actions
      updateBodyStats: async (stats) => {
        try {
          const success = await db.updateUserStats(stats);
          if (success) {
            set((state) => ({
              user: {
                ...state.user,
                ...stats
              }
            }));
          }
        } catch (error) {
          console.error('Failed to update user stats:', error);
          throw error; // Re-throw to allow UI to handle the error
        }
      },

      calculateDailyCalorieTarget: () => {
        const user = get().user;
        if (user.age && user.gender && user.height && user.weight && user.activityLevel) {
          const bmr = 10 * user.weight + 6.25 * user.height - 5 * user.age + (user.gender === 'male' ? 5 : -161);
          const activityMultiplier = {
            sedentary: 1.2,
            light: 1.375,
            moderate: 1.55,
            active: 1.725,
            very_active: 1.9
          }[user.activityLevel];
          const dailyCalorieTarget = Math.round(bmr * activityMultiplier);
          return dailyCalorieTarget;
        }
        return null;
      },
      
          // Nutrition Update Event Actions
    triggerNutritionUpdate: () => {
      set((state) => ({
        nutritionUpdateTrigger: Date.now()
      }));
    },
    
    // Meal Planning Actions
    loadMealPlan: async (startDate: string, endDate: string) => {
      try {
        const mealPlan = await db.getMealPlan(startDate, endDate);
        return mealPlan;
      } catch (error) {
        console.error('Store: Failed to load meal plan:', error);
        return null;
      }
    },
    
    addMealToWeek: async (date: string, mealType: 'breakfast' | 'lunch' | 'dinner', recipe: Recipe) => {
      try {
        const success = await db.addMealToWeek(date, mealType, recipe);
        if (success) {
          // Trigger state update if needed
          get().triggerNutritionUpdate();
        }
        return success;
      } catch (error) {
        console.error('Store: Failed to add meal to week:', error);
        return false;
      }
    },
    
    removeMealFromWeek: async (date: string, mealType: 'breakfast' | 'lunch' | 'dinner') => {
      try {
        const success = await db.removeMealFromWeek(date, mealType);
        if (success) {
          // Trigger state update if needed
          get().triggerNutritionUpdate();
        }
        return success;
      } catch (error) {
        console.error('Store: Failed to remove meal from week:', error);
        return false;
      }
    },

    shoppingLists: [],
    
    loadShoppingLists: async () => {
      try {
        const db = DatabaseService.getInstance();
        const shoppingLists = await db.getShoppingLists();
        set({ shoppingLists });
      } catch (error) {
        console.error('❌ [Store] Error loading shopping lists:', error);
      }
    },

    createShoppingList: async (shoppingListData) => {
      try {
        const db = DatabaseService.getInstance();
        const newShoppingList = await db.createShoppingList(shoppingListData);
        
        if (newShoppingList) {
          set(state => ({
            shoppingLists: [newShoppingList, ...state.shoppingLists]
          }));
          return newShoppingList;
        }
        return null;
      } catch (error) {
        console.error('❌ [Store] Error creating shopping list:', error);
        return null;
      }
    },

    updateShoppingListItem: async (itemId, updates) => {
      try {
        const db = DatabaseService.getInstance();
        const success = await db.updateShoppingListItem(itemId, updates);
        
        if (success) {
          set(state => ({
            shoppingLists: state.shoppingLists.map(list => ({
              ...list,
              items: list.items.map(item => 
                item.id === itemId 
                  ? { ...item, ...updates }
                  : item
              )
            }))
          }));
          return true;
        }
        return false;
      } catch (error) {
        console.error('❌ [Store] Error updating shopping list item:', error);
        return false;
      }
    },

    toggleShoppingListItem: async (itemId) => {
      try {
        const currentItem = get().shoppingLists
          .flatMap(list => list.items)
          .find(item => item.id === itemId);
        
        if (!currentItem) return false;
        
        return await get().updateShoppingListItem(itemId, {
          isPurchased: !currentItem.isPurchased
        });
      } catch (error) {
        console.error('❌ [Store] Error toggling shopping list item:', error);
        return false;
      }
    },

    deleteShoppingListItem: async (itemId) => {
      try {
        const db = DatabaseService.getInstance();
        const success = await db.deleteShoppingListItem(itemId);
        
        if (success) {
          set(state => ({
            shoppingLists: state.shoppingLists.map(list => ({
              ...list,
              items: list.items.filter(item => item.id !== itemId)
            }))
          }));
          return true;
        }
        return false;
      } catch (error) {
        console.error('❌ [Store] Error deleting shopping list item:', error);
        return false;
      }
    },

    deleteShoppingList: async (listId) => {
      try {
        const db = DatabaseService.getInstance();
        const success = await db.deleteShoppingList(listId);
        
        if (success) {
          set(state => ({
            shoppingLists: state.shoppingLists.filter(list => list.id !== listId)
          }));
          return true;
        }
        return false;
      } catch (error) {
        console.error('❌ [Store] Error deleting shopping list:', error);
        return false;
      }
    },

    addShoppingListItem: async (item) => {
      try {
        const db = DatabaseService.getInstance();
        const newItem = await db.addShoppingListItem(item);
        
        if (newItem) {
          // Refresh shopping lists to get the latest data
          await get().loadShoppingLists();
          return newItem;
        }
        return null;
      } catch (error) {
        console.error('❌ [Store] Error adding shopping list item:', error);
        return null;
      }
    },

    updateShoppingListItemName: async (itemId, newName) => {
      try {
        const db = DatabaseService.getInstance();
        const success = await db.updateShoppingListItemName(itemId, newName);
        
        if (success) {
          set(state => ({
            shoppingLists: state.shoppingLists.map(list => ({
              ...list,
              items: list.items.map(item => 
                item.id === itemId 
                  ? { ...item, name: newName }
                  : item
              )
            }))
          }));
          return true;
        }
        return false;
      } catch (error) {
        console.error('❌ [Store] Error updating shopping list item name:', error);
        return false;
      }
    },

          // Onboarding State
      onboarding: {
        isFirstTimeUser: false,
        showOnboarding: false,
        hasCompletedOnboarding: false,
      },

      // Onboarding Actions
      checkFirstTimeUser: () => {
        const state = get();
        const user = state.user;
        
        // Never trigger onboarding on public routes
        const publicRoutes = [
          '/',                // Landing page
          '/auth', 
          '/auth/login', 
          '/auth/signup',
          '/auth/forgot-password',
          '/auth/reset-password',
          '/help-center',
          '/contact',
          '/privacy-policy',
          '/terms-of-service'
        ];
        const currentPath = window.location.pathname;
        const isPublicRoute = publicRoutes.includes(currentPath) || 
                             currentPath.startsWith('/auth');
        
        if (isPublicRoute) {
          console.log('🚫 Skipping onboarding check - on public route:', currentPath);
          return;
        }
        
        // Checking first-time user status
        
        // Don't show onboarding if user has already completed it
        if (state.onboarding.hasCompletedOnboarding) {
          // User has completed onboarding, skipping
          return;
        }
        
        // Check if user needs onboarding based on missing essential profile data
        const needsOnboarding = (
          !user.age || // No age set
          !user.gender || // No gender set
          !user.height || // No height set
          !user.weight // No weight set
          // Note: preferences and allergies are optional and set during onboarding
        );

        console.log('🎯 Needs onboarding:', needsOnboarding);

        if (needsOnboarding) {
          console.log('🚀 Showing onboarding modal');
          set((state) => ({
            onboarding: {
              ...state.onboarding,
              isFirstTimeUser: true,
              showOnboarding: true
            }
          }));
        } else {
          console.log('✅ Profile is complete, no onboarding needed');
          // Automatically mark onboarding as complete if profile is complete
          // This prevents future unnecessary checks
          set((state) => ({
            onboarding: {
              ...state.onboarding,
              hasCompletedOnboarding: true
            }
          }));
          console.log('🎉 Automatically marked onboarding as completed');
        }
      },

      setOnboardingComplete: () => {
        console.log('🎉 Onboarding completed! User will not see it again.');
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            isFirstTimeUser: false,
            showOnboarding: false,
            hasCompletedOnboarding: true
          }
        }));
      },

      hideOnboarding: () => {
        console.log('👋 Hiding onboarding modal (will show again on next load)');
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            showOnboarding: false
            // Don't set hasCompletedOnboarding to true - only setOnboardingComplete does that
          }
        }));
      },
    }),
    {
      name: 'nosh-store',
      // Don't persist theme - we handle it manually
      partialize: (state) => ({
        // Persist onboarding state so it shows again after refresh
        onboarding: state.onboarding,
        // Don't persist imageRecognition state - it should be reset on app restart
        // Only persist other UI state if needed in future
      }),
      // Remove theme rehydration since we handle it manually
      onRehydrateStorage: () => {
        // Starting store rehydration
        return (state) => {
                      // Store rehydrated
          if (state?.onboarding) {
            // Rehydrated onboarding state
            
            // Safety check: Never show onboarding on public routes
            const publicRoutes = [
              '/',                // Landing page
              '/auth', 
              '/auth/login', 
              '/auth/signup',
              '/auth/forgot-password',
              '/auth/reset-password',
              '/help-center',
              '/contact',
              '/privacy-policy',
              '/terms-of-service'
            ];
            const currentPath = window.location.pathname;
            const isPublicRoute = publicRoutes.includes(currentPath) || 
                                 currentPath.startsWith('/auth');
            
            if (isPublicRoute && state.onboarding.showOnboarding) {
              console.log('🚫 Resetting onboarding state - on public route:', currentPath);
              // Reset the onboarding state to prevent modal showing on public routes
              // Use setTimeout to ensure this runs after rehydration is complete
              setTimeout(() => {
                useNoshStore.getState().hideOnboarding();
              }, 0);
            }
          }
        };
      },
    }
  )
);

function daysUntilExpiry(expiryDate: string): number {
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}